import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Bug,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  RefreshCw,
  Settings,
  X,
} from 'lucide-react';

import LaikaAvatar from './LaikaAvatar';

interface LaikaChatHeaderProps {
  isFullscreen: boolean;
  docked?: boolean;
  onNewConversation: () => void;
  onReportBug: () => void;
  onOpenSettings: () => void;
  onToggleFullscreen: () => void;
  onClose: () => void;
}

const LaikaChatHeader: React.FC<LaikaChatHeaderProps> = ({
  isFullscreen,
  docked = false,
  onNewConversation,
  onReportBug,
  onOpenSettings,
  onToggleFullscreen,
  onClose,
}) => {
  const { t } = useTranslation('messaging');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const menuItems = [
    {
      key: 'new',
      label: t('chatWidget.newConversation'),
      icon: RefreshCw,
      onClick: () => {
        onNewConversation();
        setMenuOpen(false);
      },
    },
    {
      key: 'bug',
      label: t('chatWidget.reportBug'),
      icon: Bug,
      onClick: () => {
        onReportBug();
        setMenuOpen(false);
      },
    },
    {
      key: 'settings',
      label: t('chatWidget.settings'),
      icon: Settings,
      onClick: () => {
        onOpenSettings();
        setMenuOpen(false);
      },
    },
  ];

  return (
    <div className="chat-header-modern">
      <div className="chat-header-info">
        <LaikaAvatar size={40} showStatus />
        <div className="chat-header-text">
          <div className="chat-header-title-row">
            <h3 className="chat-header-title">{t('chatWidget.assistantName')}</h3>
            <span
              className="chat-header-beta"
              title={t('chatWidget.testPhaseBanner')}
            >
              {t('chatWidget.betaBadge')}
            </span>
          </div>
          <p className="chat-header-status">
            {docked ? t('chatWidget.cgvDockRole') : t('chatWidget.assistantRole')}
          </p>
        </div>
      </div>

      <div className="chat-header-actions">
        <div className="chat-header-menu" ref={menuRef}>
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setMenuOpen((open) => !open)}
            className="chat-action-button"
            title={t('chatWidget.moreActions')}
            aria-label={t('chatWidget.moreActions')}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <MoreHorizontal size={18} />
          </motion.button>

          {menuOpen && (
            <div className="chat-header-dropdown" role="menu">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    className="chat-header-dropdown-item"
                    onClick={item.onClick}
                  >
                    <Icon size={16} aria-hidden />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!docked ? (
        <motion.button
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onToggleFullscreen}
          className="chat-action-button"
          title={isFullscreen ? t('chatWidget.minimize') : t('chatWidget.fullscreen')}
          aria-label={isFullscreen ? t('chatWidget.minimize') : t('chatWidget.fullscreen')}
        >
          {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </motion.button>
        ) : null}

        <motion.button
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
          className="chat-close-button"
          aria-label={t('chatWidget.close')}
        >
          <X size={20} />
        </motion.button>
      </div>
    </div>
  );
};

export default LaikaChatHeader;
